
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatMetauxAveyron = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Métaux Aveyron (12) - Rodez, Millau | Groupe Fabre"
        description="Rachat de métaux et ferrailles en Aveyron : Rodez, Millau, Villefranche-de-Rouergue. Solutions rurales, prix attractifs, service adapté."
        canonical="/nos-services/achat-metaux/aveyron"
        keywords="achat métaux Aveyron, rachat ferraille Rodez, achat cuivre Millau, ferrailleur Aveyron 12"
        localBusiness={{ city: "Rodez", region: "Aveyron", postalCode: "12000", serviceType: "Achat de métaux" }}
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Shield className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat Métaux Aveyron (12)</h1>
                  <p className="text-xl mt-2 opacity-90">Causses et Rouergue - Solutions rurales</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de métaux en Aveyron - Territoire du Rouergue
                </h2>
                <p className="text-gray-600 mb-6">
                  L'<strong>Aveyron</strong> est notre territoire d'origine, où le Groupe Fabre a développé 
                  son expertise depuis des décennies. Nous proposons des <strong>solutions adaptées</strong> 
                  aux spécificités rurales et industrielles de ce département authentique.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Nos secteurs d'intervention</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white mr-3">•</span>
                    <span><strong>Rodez :</strong> Préfecture et agglomération</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white mr-3">•</span>
                    <span><strong>Millau :</strong> Vallée du Tarn et cuirs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white mr-3">•</span>
                    <span><strong>Villefranche :</strong> Bastides et Rouergue</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white mr-3">•</span>
                    <span><strong>Decazeville :</strong> Bassin minier historique</span>
                  </li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                  <h4 className="font-bold text-blue-800 mb-3">🏠 Proximité territoriale</h4>
                  <ul className="text-blue-700 text-sm space-y-2">
                    <li>• Connaissance parfaite du territoire</li>
                    <li>• Relations de confiance établies</li>
                    <li>• Service de proximité personnalisé</li>
                    <li>• Engagement local depuis des décennies</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Service Aveyron</h3>
                <p className="text-gray-600 mb-6">
                  En tant qu'entreprise aveyronnaise, nous offrons le meilleur service 
                  avec une parfaite connaissance des besoins locaux.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 text-blue-500 mr-3" />
                    <span>Confiance locale</span>
                  </div>
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-blue-500 mr-3" />
                    <span>Prix préférentiels</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-blue-500 mr-3" />
                    <span>Service personnalisé</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-blue-500 text-white rounded-md font-medium hover:bg-blue-600 transition-colors">
                    Demander prix Aveyron
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Spécificités Aveyron */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Spécialisations sectorielles Aveyron
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Agriculture & Élevage</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Matériel agricole, équipements d'élevage
                </p>
                <div className="text-green-600 font-medium">Tarifs agriculteurs</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Industrie du Cuir</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Machines cuir, équipements spécialisés
                </p>
                <div className="text-blue-600 font-medium">Expertise Millau</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">BTP Rural</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Chantiers ruraux, rénovation patrimoine
                </p>
                <div className="text-orange-600 font-medium">Service territorial</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatMetauxAveyron;
